/**
 * Identity Platform（Firebase Authentication の GCP 版）との接続。
 *
 * ローカル開発では認証基盤を立てずに動かせるよう、`VITE_AUTH_MODE=dev` のときは
 * メールアドレスをそのままトークンとして送る実装に切り替える（バックエンドの DevVerifier と対）。
 * 本番ビルドでは dev を選べないようにしている。
 */

import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth';
import { initializeApp, type FirebaseApp } from 'firebase/app';

export type AuthMode = 'identity-platform' | 'dev';

/**
 * 本番ビルドは常に Identity Platform。開発時は明示したときだけ Identity Platform を使い、
 * 既定では認証基盤なしで動かせるようにする（Firebase の設定がないと初期化に失敗するため）。
 */
export const AUTH_MODE: AuthMode =
  import.meta.env.PROD || import.meta.env.VITE_AUTH_MODE === 'identity-platform'
    ? 'identity-platform'
    : 'dev';

export interface AuthUser {
  email: string;
  displayName: string | null;
}

export interface AuthAdapter {
  /** 現在のログイン状態を購読する。戻り値は購読解除 */
  subscribe(listener: (user: AuthUser | null) => void): () => void;
  signInWithPassword(email: string, password: string): Promise<void>;
  signInWithGoogle(): Promise<void>;
  signOut(): Promise<void>;
  getToken(): Promise<string | null>;
}

/** Identity Platform が返すエラーを利用者向けの文言にする */
function toMessage(error: unknown): string {
  const code = (error as { code?: string }).code ?? '';
  if (
    code === 'auth/invalid-credential' ||
    code === 'auth/wrong-password' ||
    code === 'auth/user-not-found' ||
    code === 'auth/invalid-email'
  ) {
    // アカウントの存在を明かさないため、原因を区別せず同じ文言にする
    return 'IDまたはパスワードが正しくありません';
  }
  if (code === 'auth/user-disabled') {
    return 'このアカウントは利用できません。管理部にお問い合わせください';
  }
  if (code === 'auth/too-many-requests') {
    return '試行回数が上限に達しました。しばらくしてからやり直してください';
  }
  if (code === 'auth/popup-closed-by-user') {
    return 'ログインが中断されました';
  }
  return 'ログインできませんでした。時間をおいてやり直してください';
}

class IdentityPlatformAdapter implements AuthAdapter {
  private readonly auth: Auth;
  private current: User | null = null;

  constructor() {
    const app: FirebaseApp = initializeApp({
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_GCP_PROJECT_ID,
    });
    this.auth = getAuth(app);
  }

  subscribe(listener: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(this.auth, (user) => {
      this.current = user;
      listener(user ? { email: user.email ?? '', displayName: user.displayName } : null);
    });
  }

  async signInWithPassword(email: string, password: string): Promise<void> {
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
    } catch (error) {
      throw new Error(toMessage(error));
    }
  }

  async signInWithGoogle(): Promise<void> {
    try {
      await signInWithPopup(this.auth, new GoogleAuthProvider());
    } catch (error) {
      throw new Error(toMessage(error));
    }
  }

  async signOut(): Promise<void> {
    await signOut(this.auth);
  }

  async getToken(): Promise<string | null> {
    if (!this.current) return null;
    // 期限切れの場合は Firebase SDK が自動で更新する
    return this.current.getIdToken();
  }
}

const DEV_STORAGE_KEY = 'meisui.dev-user';

/** ローカル開発用。メールアドレスを利用者の指定としてそのまま送る */
class DevAdapter implements AuthAdapter {
  private listeners = new Set<(user: AuthUser | null) => void>();

  private read(): string | null {
    return window.localStorage.getItem(DEV_STORAGE_KEY);
  }

  subscribe(listener: (user: AuthUser | null) => void): () => void {
    this.listeners.add(listener);
    const email = this.read();
    listener(email ? { email, displayName: null } : null);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const email = this.read();
    for (const listener of this.listeners) {
      listener(email ? { email, displayName: null } : null);
    }
  }

  async signInWithPassword(email: string): Promise<void> {
    if (!email.includes('@')) {
      throw new Error('開発モードではメールアドレスでログインします');
    }
    window.localStorage.setItem(DEV_STORAGE_KEY, email);
    this.notify();
  }

  async signInWithGoogle(): Promise<void> {
    throw new Error('開発モードでは Google ログインを利用できません');
  }

  async signOut(): Promise<void> {
    window.localStorage.removeItem(DEV_STORAGE_KEY);
    this.notify();
  }

  async getToken(): Promise<string | null> {
    const email = this.read();
    return email ? `dev:${email}` : null;
  }
}

let adapter: AuthAdapter | null = null;

/** 認証基盤への接続は初回利用時に作る（テストや開発で Firebase の初期化を走らせないため） */
export function getAuthAdapter(): AuthAdapter {
  adapter ??= AUTH_MODE === 'dev' ? new DevAdapter() : new IdentityPlatformAdapter();
  return adapter;
}
